import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import { injectIntl, defineMessages } from "react-intl";
import { withAlert } from "react-alert";
import { withStyles } from "@material-ui/core";
import { Input, Table, Badge } from "antd";
import moment from "moment";

import {
  DV3BackIcon,
  DV3SearchIcon,
  FilterIcon,
} from "../../../../common/icons";
import HUBContext from "../../../../services/HUBContext";
import CustomPaginator from "../../../../helpers/customPaginator";
import LoadInWidget from "../../../../helpers/loadInWidget";
import handleAPI from "../../../../helpers/handleAPI";
import { getSynchronizedHistoricList } from "../../../../services/api/hubcatalog/single-registry-service";
import { trackEvent } from "../../../../services/telemetry/appInsights";
import { DV3BigExclamationFilledIcon } from "../../../../common/icons";
import { styles as stylesSyncHistory } from "./VehicleSingleRegistrySyncHistoryPage.styles";
import { STATUS_GROUPS, SYNC_STATUS, translations } from "./constants";
import { FiltersModal } from "./FiltersModal";

const messages = defineMessages(translations);
const defaultPageSize = 10;

export const styles = (theme) => stylesSyncHistory;

export class VehicleSingleRegistrySyncHistoryPageBase extends React.Component {
  state = {
    search: "",
    response: null,
    loading: false,
    showFilterModal: false,
    appliedFilters: {},
    companyID: 0,
  };

  mount = false;

  statusOptions = (intl) => [
    {
      value: STATUS_GROUPS.PROCESSING,
      label: intl.formatMessage(messages.statusProcessing),
    },
    {
      value: STATUS_GROUPS.COMPLETED,
      label: intl.formatMessage(messages.statusCompleted),
    },
    {
      value: STATUS_GROUPS.FAILED,
      label: intl.formatMessage(messages.statusFailed),
    },
  ];

  platformOptions = (intl) => [
    {
      value: intl.formatMessage(messages.platformOptionsFueling),
      label: intl.formatMessage(messages.platformOptionsFueling),
      code: "TCARD",
    },
    {
      value: intl.formatMessage(messages.platformOptionsMaintenance),
      label: intl.formatMessage(messages.platformOptionsMaintenance),
      code: "TFLEET",
    },
  ];

  operationOptions = (intl) => [
    {
      value: intl.formatMessage(messages.operationCreate),
      label: intl.formatMessage(messages.operationCreate),
      code: "POST",
    },
    {
      value: intl.formatMessage(messages.operationUpdate),
      label: intl.formatMessage(messages.operationUpdate),
      code: "PUT",
    },
    {
      value: intl.formatMessage(messages.operationDelete),
      label: intl.formatMessage(messages.operationDelete),
      code: "DELETE",
    },
  ];

  isFailureStatus = (status) => {
    const normalizedStatus = String(status);

    return (
      normalizedStatus === SYNC_STATUS.FAILED ||
      normalizedStatus === SYNC_STATUS.CANCELLED ||
      normalizedStatus === SYNC_STATUS.EXPIRED
    );
  };

  getDefaultPageSize() {
    return defaultPageSize;
  }

  splitMessageIntoLines = (message, maxCharacters = 90) => {
    const text = String(message || "").trim();

    if (!text) {
      return ["N/A"];
    }

    const lines = [];
    let remainingText = text;

    while (remainingText.length > maxCharacters) {
      let breakIndex = remainingText.lastIndexOf(" ", maxCharacters);

      if (breakIndex <= 0) {
        breakIndex = maxCharacters;
      }

      lines.push(remainingText.substring(0, breakIndex));
      remainingText = remainingText.substring(breakIndex).trim();
    }

    if (remainingText) {
      lines.push(remainingText);
    }

    return lines;
  };

  columns = (intl) => [
    ...(this.countErrorsPerLine() > 0
      ? [
          {
            title: "",
            key: "errorIcon",
            width: 56,
            align: "center",
            render: (_, record) => {
              return this.isFailureStatus(record.status) ? (
                <span className={this.props.classes.errorIconCell}>
                  <DV3BigExclamationFilledIcon />
                </span>
              ) : null;
            },
          },
        ]
      : []),
    {
      title: intl.formatMessage(messages.plate),
      dataIndex: "plate",
      key: "plate",
      sorter: false,
    },
    {
      title: intl.formatMessage(messages.user),
      dataIndex: "userName",
      key: "userName",
      sorter: false,
    },
    {
      title: intl.formatMessage(messages.lastUpdate),
      dataIndex: "latestUpdate",
      key: "latestUpdate",
      sorter: false,
      render: (text) => {
        const date = this.formatDateTimeWithTimeZone(text);
        if (!date) return "";
        return intl.formatMessage(messages.dateTime, {
          date: date.format("DD/MM/YYYY"),
          time: date.format("HH[h]mm"),
        });
      },
    },
    {
      title: intl.formatMessage(messages.operation),
      dataIndex: "operation",
      key: "operation",
      sorter: false,
      render: (text) => {
        const operation = this.operationOptions(intl).find(
          (o) => o.value === text || o.code === text,
        );
        return operation ? operation.label : text;
      },
    },
    {
      title: intl.formatMessage(messages.clientCode),
      dataIndex: "clientCode",
      key: "clientCode",
      sorter: false,
    },
    {
      title: intl.formatMessage(messages.platform),
      dataIndex: "platform",
      key: "platform",
      sorter: false,
      render: (text) => {
        const platform = this.platformOptions(intl).find(
          (p) => p.value === text || p.code === text,
        );
        return platform ? platform.label : text;
      },
    },
    {
      title: intl.formatMessage(messages.status),
      dataIndex: "status",
      key: "status",
      sorter: false,
      render: (text) => {
        let label = "";
        let colorClass = "";

        switch (String(text)) {
          case SYNC_STATUS.PENDING:
          case SYNC_STATUS.PROCESSING:
          case SYNC_STATUS.RETRYING:
            label = intl.formatMessage(messages.statusProcessing);
            colorClass = "inactive";
            break;
          case SYNC_STATUS.COMPLETED:
            label = intl.formatMessage(messages.statusCompleted);
            colorClass = "active";
            break;
          case SYNC_STATUS.FAILED:
          case SYNC_STATUS.CANCELLED:
          case SYNC_STATUS.EXPIRED:
            label = intl.formatMessage(messages.statusFailed);
            colorClass = "canceled";
            break;
          default:
            const status = this.statusOptions(intl).find(
              (s) => s.value === String(text),
            );
            label = status ? status.label : text;
            colorClass = "gray";
        }

        return <span className={`v3-tag ${colorClass}`}>{label}</span>;
      },
    },
    {
      title: intl.formatMessage(messages.message),
      dataIndex: "action",
      key: "action",
      render: (text) => {
        const lines = this.splitMessageIntoLines(text);

        return (
          <span className={this.props.classes.breakWord}>
            {lines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </span>
        );
      },
    },
  ];

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value });
  };

  handleSearchSubmit = () => {
    const { response, search } = this.state;
    this.getData(
      response?.pageSize || this.getDefaultPageSize(),
      0,
      response?.sortProperty,
      response?.sortDirection,
      search,
    );
  };

  onSearchEnter = (e) => {
    const value = e.target?.value || this.state.search;

    if (this.state.search !== value) {
      this.setState({ search: value }, this.handleSearchSubmit);
    } else {
      this.handleSearchSubmit();
    }
  };

  onTableChange = (_, __, sorter) => {
    const { response, search } = this.state;
    const sortProperty = sorter.field;
    const sortDirection = sorter.order === "descend" ? "desc" : "asc";

    this.getData(
      response?.pageSize || this.getDefaultPageSize(),
      0,
      sortProperty,
      sortDirection,
      search,
    );
  };

  getData = (take, skip, sortProperty, sortDirection, search) => {
    if (!this.mount) return;

    const { intl, alert } = this.props;
    const { response, appliedFilters } = this.state;
    const { company } = this.context;

    if (!company?.companyID) return;

    let pageSize = take || response?.pageSize || this.getDefaultPageSize();
    let pageIndex =
      !response?.data && !response?.items
        ? 0
        : parseInt((skip || 0) / pageSize);

    const filters = {
      search: search || "",
      status: appliedFilters?.status || "",
      platform: appliedFilters?.platform || "",
      operation: appliedFilters?.operation || "",
      pageIndex,
      pageSize,
      sortProperty: sortProperty || "latestUpdate",
      sortDirection: sortDirection || "desc",
    };

    if (search) {
      trackEvent("SingleRegistry_SyncHistorySearched", {
        companyId: company?.companyID,
        searchPlate: search,
      });
    }

    if (
      appliedFilters?.status ||
      appliedFilters?.platform ||
      appliedFilters?.operation
    ) {
      trackEvent("SingleRegistry_SyncHistoryFiltersApplied", {
        companyId: company?.companyID,
        status: appliedFilters?.status || null,
        platform: appliedFilters?.platform || null,
        operation: appliedFilters?.operation || null,
      });
    }

    this.setState({ loading: true });

    getSynchronizedHistoricList(company?.companyID || 0, filters)
      .then((r) => {
        if (!this.mount) return;
        this.setState({ loading: false, response: r.data });
      })
      .catch((error) => {
        if (!this.mount) return;
        this.setState({ loading: false });
        handleAPI(error, alert, intl);
      });
  };

  onFilterChange = (filters) => {
    const { response } = this.state;

    this.setState(
      {
        appliedFilters: filters,
        showFilterModal: false,
      },
      () => {
        this.getData(
          response?.pageSize || this.getDefaultPageSize(),
          0,
          response?.sortProperty,
          response?.sortDirection,
          response?.search,
        );
      },
    );
  };

  openFilterModal = () => {
    this.setState({ showFilterModal: true });
  };

  closeFilterModal = () => {
    this.setState({ showFilterModal: false });
  };

  formatDateTimeWithTimeZone = (utcDate) => {
    if (!utcDate) return "";

    const utcString = utcDate.endsWith("Z") ? utcDate : `${utcDate}Z`;

    return moment.utc(utcString).local();
  };

  componentDidMount() {
    this.mount = true;
    const { setCurrentDashboard, intl, company, setCompanyID } = this.context;
    const { match } = this.props;

    trackEvent("SingleRegistry_SyncHistoryOpened", {
      companyId: match.params.cid,
    });

    setCurrentDashboard(null, intl, () => {
      if (!!document.body.classList.contains("dash-v2"))
        document.body.classList.remove("dash-v2");

      if (!document.body.classList.contains("dash-v3"))
        document.body.classList.add("dash-v3");
    });

    const inUrlCompanyID = isNaN(match.params.cid)
      ? 0
      : parseInt(match.params.cid);
    if (!!company && company.companyID !== inUrlCompanyID) {
      typeof setCompanyID === "function" && setCompanyID(inUrlCompanyID);
    }

    this.setState(
      {
        companyID: company?.companyID || 0,
      },
      () => {
        if (this.state.companyID > 0) this.getData();
      },
    );
  }

  componentDidUpdate() {
    if (!this.mount) return;

    const { match } = this.props;
    const { companyID } = this.state;
    const { company, setCompanyID } = this.context;

    const inUrlCompanyID = isNaN(match.params.cid)
      ? 0
      : parseInt(match.params.cid);

    if (!!company) {
      if (inUrlCompanyID !== company.companyID) {
        typeof setCompanyID === "function" && setCompanyID(inUrlCompanyID);
      } else if (company.companyID !== companyID) {
        this.setState({ companyID: company.companyID }, () => {
          this.getData();
        });
      }
    }
  }

  componentWillUnmount() {
    this.mount = false;
    if (document.body.classList.contains("dash-v3"))
      document.body.classList.remove("dash-v3");

    if (document.body.classList.contains("no-db-body-padding"))
      document.body.classList.remove("no-db-body-padding");
  }

  getBackURL({ company, dashboards }) {
    let vehiclesDashboardID =
      (dashboards || []).find((x) => x.dashboardKey === "vehicles-v2")
        ?.dashboardID || 0;
    if (vehiclesDashboardID <= 0)
      vehiclesDashboardID =
        (dashboards || []).find((x) => x.dashboardKey === "Vehicles")
          ?.dashboardID || 0;
    return `/app/${company?.companyID || 0}/dash/${vehiclesDashboardID}`;
  }

  countErrorsPerLine() {
    const rows =
      this.state.response?.data ||
      this.state.response?.items ||
      (Array.isArray(this.state.response) ? this.state.response : []);

    return rows.filter((row) => this.isFailureStatus(row.status)).length;
  }
}

export class VehicleSingleRegistrySyncHistoryPage extends VehicleSingleRegistrySyncHistoryPageBase {
  render() {
    const { intl, classes } = this.props;
    const { search, response, loading, showFilterModal, appliedFilters } =
      this.state;
    const { company, dashboards } = this.context;

    const filterApplied = Object.values(appliedFilters || {}).filter(
      (v) => v !== undefined && v !== null && v !== "",
    ).length;

    return (
      <Fragment>
        <div className="dashboard-area">
          <div className="dashboard-area-back">
            <Link
              className="v3-button secondary"
              to={this.getBackURL({ company, dashboards })}
            >
              <DV3BackIcon /> {intl.formatMessage(messages.goBack)}
            </Link>
          </div>
          <div className="dashboard-area-header">
            <div className={`header-title ${classes.headerTitle}`}>
              <span>{intl.formatMessage(messages.title)}</span>
            </div>
          </div>

          <div className="dashboard-area-info"></div>

          <div className="dashboard-area-widgets">
            <div className="free-widgets-container">
              <div className="card">
                <div className={classes.cardHeader}>
                  <span className={classes.subtitleText}>
                    {intl.formatMessage(messages.subtitle)}
                  </span>
                </div>

                <div className="content-card">
                  <div className={`filters-area ${classes.filtersArea}`}>
                    <div
                      className={`filters-area-search ${classes.filtersSearch}`}
                    >
                      <Input
                        value={search}
                        onChange={this.handleSearchChange}
                        onPressEnter={this.onSearchEnter}
                        placeholder={intl.formatMessage(
                          messages.searchPlaceholder,
                        )}
                        addonAfter={
                          <DV3SearchIcon onClick={this.handleSearchSubmit} />
                        }
                      />
                    </div>
                    <div className="filters-area-options">
                      <Badge count={filterApplied} overflowCount={9}>
                        <button
                          className={`simple-button ${classes.filterButton}`}
                          onClick={this.openFilterModal}
                        >
                          {intl.formatMessage(messages.filter)}
                          <FilterIcon />
                        </button>
                      </Badge>
                    </div>
                  </div>
                  <div className={`table-area ${classes.tableArea}`}>
                    <Table
                      pagination={false}
                      scroll={{ x: true }}
                      dataSource={
                        Array.isArray(response)
                          ? response
                          : response?.data || response?.items || []
                      }
                      columns={this.columns(intl).filter((x) => !!x)}
                      onChange={this.onTableChange}
                      rowKey={(record, index) => index}
                    />

                    <CustomPaginator
                      pagerInfo={response}
                      onChange={(current) =>
                        this.getData(
                          response?.pageSize || this.getDefaultPageSize(),
                          (current > 0 ? current - 1 : 0) *
                            (response?.pageSize || this.getDefaultPageSize()),
                          response?.sortProperty,
                          response?.sortDirection || "asc",
                          response?.search,
                        )
                      }
                      onShowSizeChange={(current, size) =>
                        this.getData(
                          size,
                          (current > 0 ? current - 1 : 0) * size,
                          response?.sortProperty,
                          response?.sortDirection || "asc",
                          response?.search,
                        )
                      }
                    />
                  </div>
                  {loading && <LoadInWidget />}
                  <FiltersModal
                    visible={showFilterModal}
                    intl={intl}
                    classes={classes}
                    initialFilters={appliedFilters}
                    statusOptions={this.statusOptions(intl)}
                    platformOptions={this.platformOptions(intl)}
                    operationOptions={this.operationOptions(intl)}
                    onCancel={this.closeFilterModal}
                    onChange={this.onFilterChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

VehicleSingleRegistrySyncHistoryPage.contextType = HUBContext;
export default withAlert()(
  withStyles(styles)(injectIntl(VehicleSingleRegistrySyncHistoryPage)),
);
